import React from 'react';
import {
    Flame, Leaf, BarChart3, ChevronRight, Lock, HelpCircle,
    Sparkles, Trophy, Check, X, Shield, Award, Info, ScanLine,
    Upload, Zap, Activity
} from 'lucide-react';
import { Button } from '../ui/button';
import { Link } from 'react-router-dom';

const DashboardSummary = ({
    carbonSummary,
    isPro,
    flippedCards,
    toggleFlipCard,
    getPeriodLabel,
    setShowUpgradeModal,
    complianceStatus
}) => {
    return (
        <div className="space-y-8">
            {/* Carbon Footprint Summary Section */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Carbon Footprint Summary - {getPeriodLabel()}
                    </h2>
                    <div className="flex items-center space-x-2 text-sm text-slate-500">
                        <Shield className="w-4 h-4 text-[#74A57F]" />
                        <span>Scope 1 & 2 (NGA Factors 2026)</span>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Gross Emissions Card - Flip Card */}
                    <div
                        className="relative h-56 cursor-pointer"
                        style={{ perspective: '1000px' }}
                        onClick={() => toggleFlipCard('gross')}
                        data-testid="gross-emissions-card"
                    >
                        <div
                            className={`relative w-full h-full transition-transform duration-500`}
                            style={{
                                transformStyle: 'preserve-3d',
                                transform: flippedCards.gross ? 'rotateY(180deg)' : 'rotateY(0deg)'
                            }}
                        >
                            {/* Front of card */}
                            <div
                                className="absolute inset-0 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-6 shadow-lg text-white overflow-hidden"
                                style={{ backfaceVisibility: 'hidden' }}
                            >
                                <div className="absolute top-0 right-0 w-32 h-32 transform translate-x-8 -translate-y-8">
                                    <div className="w-full h-full rounded-full bg-white/10"></div>
                                </div>
                                <div className="relative h-full flex flex-col">
                                    <div className="flex items-center mb-2">
                                        <Flame className="w-5 h-5 mr-2 opacity-80" />
                                        <span className="text-sm font-medium opacity-80">Gross Emissions</span>
                                    </div>
                                    <div className="text-4xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        {carbonSummary.gross_emissions.toFixed(2)}
                                    </div>
                                    <div className="text-sm opacity-80">tCO₂-e (total)</div>
                                    <div className="mt-auto pt-3 border-t border-white/20">
                                        <p className="text-xs opacity-70 flex items-center justify-between">
                                            <span>Direct + Indirect</span>
                                            <span className="bg-white/20 px-2 py-0.5 rounded">Click to learn more</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                            {/* Back of card */}
                            <div
                                className="absolute inset-0 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl p-6 shadow-lg text-white overflow-hidden"
                                style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                            >
                                <div className="h-full flex flex-col">
                                    <div className="flex items-center mb-3">
                                        <HelpCircle className="w-5 h-5 mr-2" />
                                        <span className="text-sm font-semibold">What is Gross Emissions?</span>
                                    </div>
                                    <p className="text-sm opacity-90 leading-relaxed flex-grow">
                                        <strong>Gross Emissions</strong> represent your total carbon dioxide equivalent (CO₂-e) produced from all activities before any offsets are applied. This includes Scope 1 (direct emissions from fuel combustion) and Scope 2 (indirect emissions from purchased electricity).
                                    </p>
                                    <div className="mt-auto pt-3 border-t border-white/20">
                                        <p className="text-xs opacity-70 flex items-center justify-center">
                                            <span className="bg-white/20 px-2 py-0.5 rounded">Click to flip back</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Carbon Offsets Card - Flip Card */}
                    <div
                        className="relative h-56 cursor-pointer"
                        style={{ perspective: '1000px' }}
                        onClick={() => toggleFlipCard('offsets')}
                        data-testid="carbon-offsets-card"
                    >
                        <div
                            className={`relative w-full h-full transition-transform duration-500`}
                            style={{
                                transformStyle: 'preserve-3d',
                                transform: flippedCards.offsets ? 'rotateY(180deg)' : 'rotateY(0deg)'
                            }}
                        >
                            {/* Front of card */}
                            <div
                                className={`absolute inset-0 rounded-2xl p-6 shadow-lg overflow-hidden ${isPro
                                        ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white'
                                        : 'bg-slate-100 text-slate-500'
                                    }`}
                                style={{ backfaceVisibility: 'hidden' }}
                            >
                                <div className="absolute top-0 right-0 w-32 h-32 transform translate-x-8 -translate-y-8">
                                    <div className={`w-full h-full rounded-full ${isPro ? 'bg-white/10' : 'bg-slate-200/50'}`}></div>
                                </div>
                                <div className="relative h-full flex flex-col">
                                    <div className="flex items-center mb-2">
                                        <Leaf className="w-5 h-5 mr-2 opacity-80" />
                                        <span className="text-sm font-medium opacity-80">Carbon Offsets</span>
                                    </div>
                                    {isPro ? (
                                        <>
                                            <div className="text-4xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                                {carbonSummary.carbon_offsets.toFixed(2)}
                                            </div>
                                            <div className="text-sm opacity-80">tCO₂-e retired</div>
                                            <div className="mt-auto pt-3 border-t border-white/20">
                                                <p className="text-xs opacity-70 flex items-center justify-between">
                                                    <Link
                                                        to="/offsets"
                                                        className="opacity-80 hover:opacity-100 flex items-center"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        Manage Offsets <ChevronRight className="w-3 h-3 ml-1" />
                                                    </Link>
                                                    <span className="bg-white/20 px-2 py-0.5 rounded">Click to learn more</span>
                                                </p>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="text-3xl font-bold mb-1 text-slate-400" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                                -
                                            </div>
                                            <div className="text-sm">Pro feature</div>
                                            <div className="mt-auto pt-3 border-t border-slate-200">
                                                <p className="text-xs flex items-center justify-between">
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setShowUpgradeModal(true); }}
                                                        className="text-[#003B5C] hover:underline flex items-center"
                                                    >
                                                        <Lock className="w-3 h-3 mr-1" /> Upgrade to track offsets
                                                    </button>
                                                    <span className="bg-slate-200 px-2 py-0.5 rounded text-slate-500">Click to learn more</span>
                                                </p>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            {/* Back of card */}
                            <div
                                className={`absolute inset-0 rounded-2xl p-6 shadow-lg overflow-hidden ${isPro
                                        ? 'bg-gradient-to-br from-emerald-600 to-emerald-700 text-white'
                                        : 'bg-slate-200 text-slate-600'
                                    }`}
                                style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                            >
                                <div className="h-full flex flex-col">
                                    <div className="flex items-center mb-3">
                                        <HelpCircle className="w-5 h-5 mr-2" />
                                        <span className="text-sm font-semibold">What are Carbon Offsets?</span>
                                    </div>
                                    <p className={`text-sm leading-relaxed flex-grow ${isPro ? 'opacity-90' : ''}`}>
                                        <strong>Carbon Offsets</strong> (or Carbon Credits) are certificates representing the removal or prevention of one tonne of CO₂-e. By purchasing and retiring offsets, you can neutralise your emissions and work towards net zero.
                                    </p>
                                    <div className={`mt-auto pt-3 border-t ${isPro ? 'border-white/20' : 'border-slate-300'}`}>
                                        <p className="text-xs opacity-70 flex items-center justify-center">
                                            <span className={`px-2 py-0.5 rounded ${isPro ? 'bg-white/20' : 'bg-slate-300'}`}>Click to flip back</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Net Emissions / Total Carbon Footprint Card - Flip Card */}
                    <div
                        className="relative h-56 cursor-pointer"
                        style={{ perspective: '1000px' }}
                        onClick={() => toggleFlipCard('net')}
                        data-testid="net-emissions-card"
                    >
                        <div
                            className={`relative w-full h-full transition-transform duration-500`}
                            style={{
                                transformStyle: 'preserve-3d',
                                transform: flippedCards.net ? 'rotateY(180deg)' : 'rotateY(0deg)'
                            }}
                        >
                            {/* Front of card */}
                            <div
                                className={`absolute inset-0 rounded-2xl p-6 shadow-lg text-white overflow-hidden ${carbonSummary.net_emissions <= 0
                                        ? 'bg-gradient-to-br from-amber-400 via-amber-500 to-yellow-500'
                                        : 'bg-gradient-to-br from-[#003B5C] to-[#004d7a]'
                                    }`}
                                style={{ backfaceVisibility: 'hidden' }}
                            >
                                <div className="absolute top-0 right-0 w-32 h-32 transform translate-x-8 -translate-y-8">
                                    <div className="w-full h-full rounded-full bg-white/10"></div>
                                </div>
                                {/* Net Zero Celebration Badge */}
                                {carbonSummary.net_emissions <= 0 && carbonSummary.gross_emissions > 0 && (
                                    <div className="absolute top-3 right-3 flex items-center space-x-1 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1">
                                        <Trophy className="w-4 h-4 text-white" />
                                        <span className="text-xs font-semibold text-white">Net Zero!</span>
                                    </div>
                                )}
                                <div className="relative h-full flex flex-col">
                                    <div className="flex items-center mb-2">
                                        <BarChart3 className="w-5 h-5 mr-2 opacity-80" />
                                        <span className="text-sm font-medium opacity-80">Total Carbon Footprint</span>
                                    </div>
                                    <div className="text-4xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        {Math.abs(carbonSummary.net_emissions) < 0.1 && Math.abs(carbonSummary.net_emissions) > 0
                                            ? carbonSummary.net_emissions.toFixed(4)
                                            : carbonSummary.net_emissions.toFixed(2)}
                                    </div>
                                    <div className="text-sm opacity-80">tCO₂-e (net)</div>
                                    <div className="mt-auto pt-3 border-t border-white/20">
                                        {carbonSummary.net_emissions <= 0 && carbonSummary.gross_emissions > 0 ? (
                                            <p className="text-xs opacity-90 flex items-center justify-between">
                                                <span className="flex items-center"><Sparkles className="w-3 h-3 mr-1" /> Net zero achieved!</span>
                                                <span className="bg-white/20 px-2 py-0.5 rounded">Click to learn more</span>
                                            </p>
                                        ) : (
                                            <p className="text-xs opacity-70 flex items-center justify-between">
                                                <span>Gross - Offsets = Net</span>
                                                <span className="bg-white/20 px-2 py-0.5 rounded">Click to learn more</span>
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {/* Back of card */}
                            <div
                                className={`absolute inset-0 rounded-2xl p-6 shadow-lg text-white overflow-hidden ${carbonSummary.net_emissions <= 0
                                        ? 'bg-gradient-to-br from-amber-500 to-amber-600'
                                        : 'bg-gradient-to-br from-[#004d7a] to-[#003B5C]'
                                    }`}
                                style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
                            >
                                <div className="h-full flex flex-col">
                                    <div className="flex items-center mb-3">
                                        <HelpCircle className="w-5 h-5 mr-2" />
                                        <span className="text-sm font-semibold">What is Carbon Footprint?</span>
                                    </div>
                                    <p className="text-sm opacity-90 leading-relaxed flex-grow">
                                        Your <strong>Total Carbon Footprint</strong> (net emissions) is calculated as: Gross Emissions minus Carbon Offsets. A negative or zero value means you've achieved <strong>Net Zero</strong> - your offsets equal or exceed your emissions!
                                    </p>
                                    <div className="mt-auto pt-3 border-t border-white/20">
                                        <p className="text-xs opacity-70 flex items-center justify-center">
                                            <span className="bg-white/20 px-2 py-0.5 rounded">Click to flip back</span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Categories tracked info */}
                {complianceStatus && (
                    <div className="mt-4 text-center">
                        <p className="text-slate-500 text-sm">
                            {complianceStatus.filter(s => s.completed).length} of {complianceStatus.length} emission categories tracked
                        </p>
                    </div>
                )}
            </div>

            {/* Net Zero Progress Bar Widget - Pro Feature */}
            {isPro && carbonSummary.gross_emissions > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-testid="net-zero-progress-widget">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                            <Trophy className={`w-5 h-5 ${carbonSummary.net_emissions <= 0 ? 'text-amber-500' : 'text-slate-400'}`} />
                            <h3 className="text-lg font-semibold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                Net Zero Journey - {getPeriodLabel()}
                            </h3>
                        </div>
                        {carbonSummary.net_emissions <= 0 && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-amber-400 to-yellow-400 text-white shadow-sm">
                                <Sparkles className="w-4 h-4 mr-1" />
                                Net Zero Achieved!
                            </span>
                        )}
                    </div>

                    {/* Progress Bar */}
                    <div className="relative">
                        {/* Background bar */}
                        <div className="h-8 bg-slate-100 rounded-full overflow-hidden relative">
                            {/* Emissions portion (red) */}
                            <div
                                className="absolute left-0 top-0 h-full bg-gradient-to-r from-red-400 to-red-500"
                                style={{
                                    width: `${Math.min(100, (carbonSummary.gross_emissions / Math.max(carbonSummary.gross_emissions, carbonSummary.carbon_offsets || 1)) * 100)}%`,
                                    transition: 'width 1s ease-out'
                                }}
                            />
                            {/* Offsets overlay (green) - grows from left */}
                            <div
                                className="absolute left-0 top-0 h-full bg-gradient-to-r from-emerald-400 to-emerald-500"
                                style={{
                                    width: `${Math.min(100, (carbonSummary.carbon_offsets / carbonSummary.gross_emissions) * 100)}%`,
                                    transition: 'width 1.2s ease-out 0.3s'
                                }}
                            />
                            {/* Net Zero line marker */}
                            <div className="absolute right-0 top-0 h-full w-1 bg-[#003B5C] flex items-center justify-center">
                                <div className="absolute -right-8 bg-[#003B5C] text-white text-xs px-2 py-0.5 rounded-full whitespace-nowrap">
                                    Net Zero
                                </div>
                            </div>
                        </div>

                        {/* Labels */}
                        <div className="flex justify-between mt-3 text-sm">
                            <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                <span className="text-slate-600">Gross: {carbonSummary.gross_emissions.toFixed(2)} tCO₂-e</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                                <span className="text-slate-600">Offsets: {carbonSummary.carbon_offsets.toFixed(2)} tCO₂-e</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <span className={`font-semibold ${carbonSummary.net_emissions <= 0 ? 'text-emerald-600' : 'text-[#003B5C]'}`}>
                                    Net: {carbonSummary.net_emissions.toFixed(2)} tCO₂-e
                                </span>
                            </div>
                        </div>

                        {/* Progress percentage */}
                        <div className="mt-4 text-center">
                            <div className="inline-flex items-center px-4 py-2 bg-slate-50 rounded-full">
                                <span className="text-sm text-slate-600 mr-2">Offset Coverage:</span>
                                <span className={`text-lg font-bold ${carbonSummary.carbon_offsets >= carbonSummary.gross_emissions
                                        ? 'text-emerald-600'
                                        : carbonSummary.carbon_offsets >= carbonSummary.gross_emissions * 0.5
                                            ? 'text-amber-600'
                                            : 'text-red-600'
                                    }`}>
                                    {carbonSummary.gross_emissions > 0
                                        ? Math.min(100, (carbonSummary.carbon_offsets / carbonSummary.gross_emissions * 100)).toFixed(0)
                                        : 0}%
                                </span>
                            </div>
                            {carbonSummary.net_emissions > 0 && (
                                <p className="text-sm text-slate-500 mt-2">
                                    {carbonSummary.net_emissions.toFixed(2)} tCO₂-e remaining to reach net zero
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DashboardSummary;
